import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { logDOM } from "@testing-library/react";
import moment from "moment";
import { saveAs } from 'file-saver';
import { formControlClasses } from "@mui/material";
import { msalInstance } from "../msalInstance";
import { protectedResources } from "../authConfig";

const getTokenFromLocalStorage = () => {
  const token = localStorage.getItem("token");
  return token;
};

// Decode a JWT's `exp` and report whether the token is missing, unparseable, already expired, or
// within `skewSeconds` of expiring. Used to refresh proactively before a request goes out — the
// fix for "refresh the browser with a stale token → the app looks logged in but no data loads."
const tokenExpiresSoon = (token, skewSeconds = 120) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (!payload?.exp) return true;
    return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
  } catch {
    return true; // can't read it → treat as expired
  }
};

// Prevents firing multiple interactive re-auth redirects at once.
let reauthInProgress = false;

// Get a valid access token from MSAL: returns the cached token when still valid, silently refreshes
// it (via the refresh token) when expired, and — when the session can no longer be renewed silently
// — clears the stale token and redirects the user to re-authenticate. Returns the token, or null if
// a re-auth redirect was started (or there's no signed-in account).
const acquireValidToken = async (forceRefresh = false) => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: protectedResources.api.scopes.read,
      account,
      forceRefresh,
    });
    localStorage.setItem("token", result.accessToken);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // The session is stale and can't be renewed without the user — prompt re-authentication.
      if (!reauthInProgress) {
        reauthInProgress = true;
        localStorage.removeItem("token");
        msalInstance
          .acquireTokenRedirect({ scopes: protectedResources.api.scopes.read, account })
          .catch(() => { reauthInProgress = false; });
      }
      return null;
    }
    // Transient/other errors (offline, etc.): keep whatever we have and let the request proceed.
    return getTokenFromLocalStorage();
  }
};

// Use environment variable for API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:7202";
/* const API_BASE_URL = "https://localhost:7202"; */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    const token = getTokenFromLocalStorage();
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Hardened base query. Before each request it guarantees a fresh, non-expired token (refreshing
// silently when needed); if the API still rejects the token with 401 it force-refreshes once and
// retries; if the token can't be renewed at all it triggers re-authentication. This closes the
// stale-token gap where a browser refresh restored the account but every data call 401'd silently.
const baseQueryWithReauth = async (args, api, extraOptions) => {
  if (tokenExpiresSoon(getTokenFromLocalStorage())) {
    await acquireValidToken();
  }
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result?.error?.status === 401) {
    const refreshed = await acquireValidToken(true); // force a fresh token, then retry once
    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const clientAPI = createApi({
  reducerPath: "clientAPI",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Client", "Clients", 'Service', 'UserSchedule', 'PlanOfCare', 'PlanOfCarePreview', 'ClientDocuments', 'Users', 'Providers', 'NeededAuthorizations', 'UrineScreenSubstance', 'Urinalysis', 'SignatureHistory', 'ClientDiagnosis', 'ClientHistory', 'ClientHousings', 'CompanyBranches', 'ClientSubstanceUse', 'ClientAssignmentHistory', 'ClientTemporaryAssignments', 'Roles'],
  endpoints: (build) => ({
    fetchClient: build.query({
      query: (id) => ({
        url: `/api/Client/${id}`,
      }),
      providesTags: (result) => ["Client"],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    postClient: build.mutation({
      query: (client) => ({
        url: `/api/Client/SaveClient`,
        method: "POST",
        body: client,
      }),
      invalidatesTags: ["Client", "ClientHistory"],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Batch-import clients + completed intake assessments from Zoobooks-format PDF exports.
    // Returns one IntakeImportResult per uploaded file (success/failure is per-file, not
    // per-batch), so this always resolves — check each result's `success` flag.
    importIntake: build.mutation({
      // { files, levelOfCareId, billable }: the level of care applies to every client in the upload.
      // Imported intakes are non-billable unless billable is true.
      queryFn: async ({ files, levelOfCareId, billable }) => {
        try {
          const formData = new FormData();
          (files || []).forEach((file) => formData.append("files", file));
          if (levelOfCareId) formData.append("levelOfCareId", String(levelOfCareId));
          formData.append("billable", billable ? "true" : "false");
          const response = await fetch(`${API_BASE_URL}/api/Client/ImportIntake`, {
            method: "POST",
            headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
            body: formData,
          });
          if (!response.ok) throw new Error("Import failed.");
          const data = await response.json();
          return { data };
        } catch (e) {
          return { error: e.message };
        }
      },
      invalidatesTags: ["Client"],
    }),
    // Field-level change history for a client record (who changed what, when) — shaped for the
    // shared ServiceHistoryDialog (same dialog used for service/billing/signature history).
    getClientHistory: build.query({
      query: (clientId) => ({
        url: `/api/Client/${clientId}/History`,
      }),
      providesTags: ["ClientHistory"],
      transformResponse: (response) =>
        (Array.isArray(response) ? response : []).map((h) => ({
          status: h.fieldName,
          statusDate: h.changeDate,
          createdBy: h.changedByName,
          note: `${h.oldValue || "(empty)"} → ${h.newValue || "(empty)"}`,
        })),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    getUserInfo: build.query({
      query: () => ({
        url: `/api/Users/Me`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getMyDailyAlerts: build.query({
      // date: "YYYY-MM-DD" (client's local today). Returns { services: [...], notes: [...] }.
      query: (date) => ({
        url: `/api/Users/Me/DailyAlerts${date ? `?date=${date}` : ''}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    getClients: build.mutation({
      query: (body) => {
        // Log the body to the console
        console.log("Request Body:", {
          recordNumber: body.recordNumber,
          offset: body.offset,
          status: body.status || 0,
          counselor: body.counselor || null,
          levelofcare: body.levelofcare || 0,
          housing: body.housing || 0,
          searchValue: body.searchVal,
          orderBy: body.orderBy,
          orderByDirection: body.order,
        });

        return {
          url: `/api/Client/GetClientsByFilter`,
          method: "POST",
          body: {
            recordNumber: body.recordNumber,
            offset: body.offset,
            status: body.status || 0,
            counselor: body.counselor || null,
            levelofcare: body.levelofcare || 0,
            housing: body.housing || 0,
            searchValue: body.searchVal,
            orderBy: body.orderBy,
            orderByDirection: body.order,
            includeOwnedServiceClients: body.includeOwnedServiceClients || false,
          },
        };
      },
      async onQueryStarted(body, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log("API Response:", data); // Log the API response here
        } catch (error) {
          console.error("API Error:", error);
        }
      },
    }),
    getBillingReport: build.mutation({
      query: (body) => {
        // Log to show billingStatus filter being passed
        console.log("getBillingReport filters:", body);

        return {
          url: `/api/ClientService/GetBillingReport`,
          method: "POST",
          body: {
            recordNumber: body.recordNumber,
            offset: body.offset,
            clientIdList: body.clientIdList || null,
            orderBy: body.orderBy,
            orderByDirection: body.order,
            serviceDate: body.serviceDate || null,
            endDate: body.endDate || null,
            serviceIdList: body.serviceIdList || null,
            levelOfCareIdList: body.levelOfCare || null,
            billingStatusIdList: body.billingStatus || null,
            serviceStatusIdList: body.serviceStatus || null,
            authorizationList: body.authorizationList || null,
            ownerList: body.serviceOwner || null,
            includeOwnedServices: body.includeOwnedServices || false,
            // Billing page only: completed, already-occurred, billable services.
            billingView: body.billingView || false,
          },
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    // Scheduling list (calendar, client services list, dashboard): the same rows, filters and
    // response shape as getBillingReport, without billing data (status, rule-check errors, rate,
    // payments, authorizations). Billing-only filters (billingStatus, authorizationList) don't apply.
    getSchedule: build.mutation({
      query: (body) => ({
        url: `/api/ClientService/GetSchedule`,
        method: "POST",
        body: {
          recordNumber: body.recordNumber,
          offset: body.offset,
          clientIdList: body.clientIdList || null,
          orderBy: body.orderBy,
          orderByDirection: body.order,
          serviceDate: body.serviceDate || null,
          endDate: body.endDate || null,
          serviceIdList: body.serviceIdList || null,
          levelOfCareIdList: body.levelOfCare || null,
          serviceStatusIdList: body.serviceStatus || null,
          ownerList: body.serviceOwner || null,
          includeOwnedServices: body.includeOwnedServices || false,
        },
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    postUpload835File: build.mutation({
      query: (body) => ({
        url: `/api/ClientService/Upload/835File`,
        method: "POST",
        body: body,
      }),
      transformResponse: (response) => {
        // Directly extract and return the message from the JSON response
        console.log("API Response (transformResponse):", response); // Debugging line
        return { message: response.message || 'File uploaded successfully' };
      },
      transformErrorResponse: (response) => {
        // Handle error response by returning the message or default error message
        console.log("API Error Response:", response); // Debugging line
        return { message: response.data?.Message || 'An error occurred' };
      },
    }),
    downloadFile837: build.mutation({
      queryFn: async (body) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/ClientService/Download/837File`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
                "Content-Type": "application/json",
              },
              method: "POST",
              body: JSON.stringify({
                recordNumber: body.recordNumber,
                offset: body.offset,
                clientIdList: body.clientIdList || null,
                orderBy: body.orderBy,
                orderByDirection: body.order,
                serviceDate: body.serviceDate || null,
                endDate: body.endDate || null,
                serviceIdList: body.serviceIdList || null,
                levelOfCareIdList: body.levelOfCare || null,
                billingStatusIdList: body.billingStatus || null,
              }),
            }
          );

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/zip')) {
            const blob = await response.blob();
            saveAs(blob, `i837_preview_${moment().format('YYYYMMDDHHmmss')}.zip`);
            return { data: { type: 'preview' } };
          } else {
            const json = await response.json();
            return { data: { type: 'no_records', message: json.message || 'No billable services found.' } };
          }
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    confirmDownloadFile837: build.mutation({
      queryFn: async (body) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/ClientService/Download/837File/Confirm`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
                "Content-Type": "application/json",
              },
              method: "POST",
              body: JSON.stringify({
                recordNumber: body.recordNumber,
                offset: body.offset,
                clientIdList: body.clientIdList || null,
                orderBy: body.orderBy,
                orderByDirection: body.order,
                serviceDate: body.serviceDate || null,
                endDate: body.endDate || null,
                serviceIdList: body.serviceIdList || null,
                levelOfCareIdList: body.levelOfCare || null,
                billingStatusIdList: body.billingStatus || null,
              }),
            }
          );

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('text/plain') || contentType.includes('application/octet-stream')) {
            const blob = await response.blob();
            saveAs(blob, `i837_${moment().format('YYYYMMDDHHmmss')}.txt`);
            return { data: { success: true, message: 'File downloaded and services marked as billed.' } };
          } else {
            const json = await response.json();
            return { data: { success: false, message: json.message || 'No billable services found.' } };
          }
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    sendFile837: build.mutation({
      queryFn: async (body) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/ClientService/SendClaimData/837File`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
                "Content-Type": "application/json",
              },
              method: "POST",
              body: JSON.stringify({
                recordNumber: body.recordNumber,
                offset: body.offset,
                clientIdList: body.clientIdList || null,
                orderBy: body.orderBy,
                orderByDirection: body.order,
                serviceDate: body.serviceDate || null,
                endDate: body.endDate || null,
                serviceIdList: body.serviceIdList || null,
                levelOfCareIdList: body.levelOfCare || null,
                billingStatusIdList: body.billingStatus || null,
                authorizationList: body.authorizationList || null,
              }),
            }
          );

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/zip')) {
            const blob = await response.blob();
            saveAs(blob, `i837_preview_${moment().format('YYYYMMDDHHmmss')}.zip`);
            return { data: { type: 'preview' } };
          } else {
            const json = await response.json();
            return { data: { type: 'no_records', message: json.Message || json.message || 'No billable services found.' } };
          }
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    confirmSendFile837: build.mutation({
      query: (body) => ({
        url: `/api/ClientService/SendClaimData/837File/Confirm`,
        method: "POST",
        body: {
          recordNumber: body.recordNumber,
          offset: body.offset,
          clientIdList: body.clientIdList || null,
          // Restricts the transmit to exactly the claims the user checked in the preview dialog.
          // Without this the SP bills every claim matching the (client-scoped) filter, so unchecking
          // claims had no effect. ClaimRootIdList is inherited by the filter model → @ClaimRootIdList SP param.
          claimRootIdList: body.claimRootIdList || null,
          orderBy: body.orderBy,
          orderByDirection: body.order,
          serviceDate: body.serviceDate || null,
          endDate: body.endDate || null,
          serviceIdList: body.serviceIdList || null,
          levelOfCareIdList: body.levelOfCare || null,
          billingStatusIdList: body.billingStatus || null,
          authorizationList: body.authorizationList || null,
        },
      }),
      // The API serializes anonymous objects as camelCase (System.Text.Json default), so read
      // camelCase first (PascalCase fallback for safety). Reading only PascalCase made billedCount
      // undefined and always fell back to the generic success message — even when 0 claims billed.
      transformResponse: (response) => ({
        message: response?.message ?? response?.Message ?? 'Claims transmitted successfully.',
        claimMdResponse: response?.claimMdResponse ?? response?.ClaimMdResponse,
        billedCount: response?.billedCount ?? response?.BilledCount ?? 0,
        skippedServices: response?.skippedServices ?? response?.SkippedServices ?? [],
      }),
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.message || 'An error occurred while transmitting claims.',
      }),
    }),
    // Live claim status from Claim.MD for a single service (billing record "Check Status" button).
    checkClaimStatus: build.mutation({
      query: (clientServiceId) => ({ url: `/api/ClientService/ClaimStatus/${clientServiceId}`, method: 'GET' }),
    }),

    // Read-only claim list for the transmit preview grid (isReview server-side — does NOT mark billed).
    getClaimDataPreview: build.mutation({
      query: (body) => ({
        url: `/api/ClientService/GetClaimData/Preview`,
        method: "POST",
        body: {
          recordNumber: body.recordNumber,
          offset: body.offset,
          clientIdList: body.clientIdList || null,
          orderBy: body.orderBy,
          orderByDirection: body.order,
          serviceDate: body.serviceDate || null,
          endDate: body.endDate || null,
          serviceIdList: body.serviceIdList || null,
          levelOfCareIdList: body.levelOfCare || null,
          billingStatusIdList: body.billingStatus || null,
          authorizationList: body.authorizationList || null,
        },
      }),
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.message || 'Failed to load claim preview.',
      }),
    }),

    // CMS-1500 PDF for one claim. Pass clientIdList narrowed to the single claim's client, and
    // claimRootIdList narrowed to that claim so clients with multiple billable claims in the
    // batch don't get every one of their claims rendered into this single-claim PDF.
    downloadClaimPdf: build.mutation({
      queryFn: async (body) => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/ClientService/GetClaimData/PDF`, {
            cache: "no-store",
            headers: { authorization: `Bearer ${getTokenFromLocalStorage()}`, "Content-Type": "application/json" },
            method: "POST",
            body: JSON.stringify({
              recordNumber: body.recordNumber, offset: body.offset,
              clientIdList: body.clientIdList || null,
              claimRootIdList: body.claimRootIdList || null,
              orderBy: body.orderBy, orderByDirection: body.order,
              serviceDate: body.serviceDate || null, endDate: body.endDate || null,
              serviceIdList: body.serviceIdList || null, levelOfCareIdList: body.levelOfCare || null,
              billingStatusIdList: body.billingStatus || null, authorizationList: body.authorizationList || null,
            }),
          });
          if (!response.ok) throw new Error('Failed to generate the claim PDF.');
          const blob = await response.blob();
          // Open in a new tab for review rather than force-download.
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener');
          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
          return { data: { type: 'claim-pdf' } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    // Excel export of the claims that would transmit (one row per service line).
    downloadClaimsExcel: build.mutation({
      queryFn: async (body) => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/ClientService/GetClaimData/Excel`, {
            cache: "no-store",
            headers: { authorization: `Bearer ${getTokenFromLocalStorage()}`, "Content-Type": "application/json" },
            method: "POST",
            body: JSON.stringify({
              recordNumber: body.recordNumber, offset: body.offset,
              clientIdList: body.clientIdList || null,
              orderBy: body.orderBy, orderByDirection: body.order,
              serviceDate: body.serviceDate || null, endDate: body.endDate || null,
              serviceIdList: body.serviceIdList || null, levelOfCareIdList: body.levelOfCare || null,
              billingStatusIdList: body.billingStatus || null, authorizationList: body.authorizationList || null,
            }),
          });
          if (!response.ok) throw new Error('Failed to export claims to Excel.');
          const blob = await response.blob();
          saveAs(blob, `ClaimsToTransmit_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
          return { data: { type: 'claims-excel' } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    getERAFiles: build.mutation({
      query: (body) => {

        return {
          url: `/api/ClientService/GetERAList`,
          method: "POST",
          body: {},
        };
      },
      // Transform successful response
      transformResponse: (response) => {
        console.log("success response:", response);
        return { message: response.message || "File processed successfully!" };
      },
      // Transform error response
      transformErrorResponse: (response) => {
        console.log("error response:", response);
        return { message: response.error || "An error occurred while processing the file." };
      },
    }),

    getProfilePicture: build.query({
      queryFn: async ({ id, resolution = "thumbnail" }) => {
        try {
          const url = resolution
            ? `${API_BASE_URL}/api/Client/${id}/ProfilePicture?resolution=${resolution}`
            : `${API_BASE_URL}/api/Client/${id}/ProfilePicture`;
          const response = await fetch(url, {
            // Revalidate with the API's ETag: an unchanged thumbnail comes back as a tiny 304
            // served from the browser cache instead of re-downloading (the API marks it private).
            cache: "no-cache",
            headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
          });
          // No photo on file — treat as empty, don't try to display an error body as an image
          if (!response.ok) return { data: null };
          const contentType = response.headers.get("content-type") || "";
          if (!contentType.startsWith("image/")) return { data: null };
          const blob = await response.blob();
          if (!blob || blob.size === 0) return { data: null };
          return { data: URL.createObjectURL(blob) };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    getSearchPossibleValues: build.query({
      query: (data) => {
        console.log(`getSearchPossibleValues called with id: ${data.id}, value: ${data.value}`);
        return {
          url: `/api/PsychosocialAssessmentForm/PossibleValues/${data.id}?searchQuery=${data.value}`,
        };
      },
      transformErrorResponse: (response) => {
        console.log(`getSearchPossibleValues response: ${response.data.Message}`);
        return { message: response.data.Message };
      },
    }),
    getPossibleValueById: build.query({
      query: (data) => {
        console.log(`getPossibleValueById called with id: ${data.id}, value: ${data.value}`);
        return {
          url: `/api/PsychosocialAssessmentForm/PossibleValue/${data.id}/${data.value}`,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAssessmentForm: build.query({
      query: ({formId, versionId}) => {
        // No specific version → send 0, which the backend resolves to the form's CURRENT version.
        // Versions don't start at 1, so sending null/undefined (or the backend default of 1) would
        // request a non-existent version and load nothing.
        const v = (versionId == null || versionId === '' || versionId === 'null' || versionId === 'undefined')
          ? 0 : versionId;
        return {
          url: `/api/PsychosocialAssessmentForm/${formId}?formVersion=${v}`,
        };
      },
  
      transformResponse: (response) => {
        const getValue = (type) => {
          switch (type) {
            case 1:
            case 7:
              return "";
            case 2:
              return null;
            case 6:
              return [];
            default:
              return 0;
          }
        };
        // "Per client" sections (group notes): each client answers them separately, so their fields
        // are NOT validated with the shared form — they're required per attended client instead.
        const perClient = { groups: [], fieldIds: [], requiredFieldIds: [] };
        const extractFields = (obj, result, validation, inPerClient = false) => {
          const perClientHere = inPerClient || !!obj.isPerClient;
          if (obj.isPerClient && !inPerClient) perClient.groups.push(obj);
          if (obj.formFields) {
            obj.formFields.forEach((field) => {
              result[field.id] = getValue(field.type);
              if (perClientHere) {
                perClient.fieldIds.push(field.id);
                if (field.required) perClient.requiredFieldIds.push(field.id);
              }
              validation[field.id] = {
                validationType: field.required && !perClientHere ? "required" : null,
                name: field.id,
              };
            });
          }
          if (obj.groups) {
            obj.groups.forEach((group) => {
              extractFields(group, result, validation, perClientHere);
            });
          }
        };
        const fields = {};
        const validation = {};

        response.group.forEach((item) => {
          extractFields(item, fields, validation);
        });
        const sections = response.group
          ?.slice()
          .sort((a, b) => a.priority - b.priority);

        const requiredSignatures = response.requiredSignatures;

        const formTitle = response.name;

        return { sections, fields, validation, requiredSignatures, formTitle, perClient };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    getAssessmentForms: build.query({
      query: () => ({
        url: `/api/PsychosocialAssessmentForm`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),

     getAssessmentFormTypes: build.query({
      query: () => ({
        url: `/api/PsychosocialAssessmentForm/Types`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),

    getAssessmentFormHistory: build.query({
      query: (formId) => ({
        url: `/api/PsychosocialAssessmentForm/FormHistory/${formId}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),

    // Form-DEFINITION version/change audit trail (who changed which field/group, and when) —
    // distinct from getAssessmentFormHistory above, which is client RESPONSE history.
    getFormVersionHistory: build.query({
      query: (formId) => ({
        url: `/api/PsychosocialAssessmentForm/${formId}/VersionHistory`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),

    // Apply a form's latest version to existing notes — preview (nothing changes).
    // { formId, fromDate: 'YYYY-MM-DD' | null } (null = all notes). Billed notes are never included.
    getFormApplyToNotesPreview: build.query({
      query: ({ formId, fromDate }) => ({
        url: `/api/PsychosocialAssessmentForm/${formId}/ApplyToNotes${fromDate ? `?fromDate=${encodeURIComponent(fromDate)}` : ''}`,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: (response) => ({
        message: response.data?.message || response.data?.Message || 'Could not check existing notes.',
      }),
    }),
    // Apply: { formId, fromDate, expectedNoteCount } (count from the preview).
    applyFormToNotes: build.mutation({
      query: ({ formId, ...body }) => ({
        url: `/api/PsychosocialAssessmentForm/${formId}/ApplyToNotes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Service', 'SignatureHistory'],
      transformErrorResponse: (response) => ({
        message: response.data?.message || response.data?.Message || 'The change could not be applied to existing notes.',
      }),
    }),
    updateAssessmentForm: build.mutation({
      query: (form) => {
        console.log("form post data", form); // Moved the console.log here
        return {
          url: `/api/PsychosocialAssessmentForm/Update`,
          method: "POST",
          body: form,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),

    // Reverts a form to an older version by copying that version's content into a brand-new
    // version, server-side — the same operation as any structural edit, just with old content.
    revertAssessmentFormVersion: build.mutation({
      query: ({ formId, version }) => ({
        url: `/api/PsychosocialAssessmentForm/${formId}/Revert/${version}`,
        method: "POST",
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),

    getAssessmentFormSignatures: build.query({
      query: ({ clientId, formId, clientServiceId, formVersion }) => ({
        url: `/api/PsychosocialAssessmentForm/${formId}/signatures?clientId=${clientId}&clientServiceId=${clientServiceId}&formVersion=${formVersion}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    signForm: build.mutation({
      query: (data) => {
        return {
          url: `/api/PsychosocialAssessmentForm/sign/${data.signatureId}?clientId=${data.userId}&clientServiceId=${data.clientServiceId}`,
          method: "PUT",
        };
      },
      // Refresh the signature-history dialog so the new 'Signed' event shows immediately.
      invalidatesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    unsignForm: build.mutation({
      query: (data) => {
        return {
          url: `/api/PsychosocialAssessmentForm/sign/${data.signatureId}?clientId=${data.userId}&clientServiceId=${data.clientServiceId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Sign once → apply to all clients in a session.
    // targets: [{ clientId, clientServiceId }]
    signGroupForm: build.mutation({
      query: ({ targetSignatureId, targets }) => ({
        url: `/api/PsychosocialAssessmentForm/sign-group/${targetSignatureId}`,
        method: "PUT",
        body: { targets },
      }),
      invalidatesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    removeGroupFormSignature: build.mutation({
      query: ({ targetSignatureId, targets }) => ({
        url: `/api/PsychosocialAssessmentForm/sign-group/${targetSignatureId}`,
        method: "DELETE",
        body: { targets },
      }),
      invalidatesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    getAssessmentValues: build.query({
      queryFn: async ({ clientId, formId, clientServiceId }) => {
        try {
          // Log clientId, formId, and clientServiceId
          console.log(`Fetching assessment values for clientId: ${clientId}, formId: ${formId}, clientServiceId: ${clientServiceId}`);

          const response = await fetch(
            `${API_BASE_URL}/api/PsychosocialAssessmentForm/GetFieldValues/${clientId}?assessmentId=${formId}&clientServiceId=${clientServiceId}`,
            {
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
            }
          );

          const data = await response.json();
          console.log("form response data", data);
          return { data: await data };
        } catch (e) {
          return { message: e.message };
        }
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    // Paginated QuestPDF export of a completed assessment (all field types resolved).
    downloadAssessmentPDF: build.mutation({
      queryFn: async ({ clientId, formId, clientServiceId, formVersion = 0, fileName }) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/PsychosocialAssessmentForm/PDF/${clientId}?assessmentId=${formId}&clientServiceId=${clientServiceId}&formVersion=${formVersion}`,
            {
              cache: "no-store",
              headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to generate assessment PDF");
          }
          const blob = await response.blob();
          saveAs(blob, fileName || `assessment_${moment().format("YYYYMMDDHHmmss")}.pdf`);
          return { data: { type: "assessment" } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    // A client's whole urinalysis history: every urine screen with its substance results, the
    // encounter date/time and the provider.
    // Requested/signed/unsigned events for one required-signature slot instance.
    getSignatureHistory: build.query({
      query: ({ signatureId, clientId, clientServiceId }) => ({
        url: `/api/PsychosocialAssessmentForm/sign/${signatureId}/History?clientId=${clientId}&clientServiceId=${clientServiceId}`,
      }),
      providesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    // Active users holding a role (legacy RoleId OR active assignment) — the request-signature picker.
    getUsersByRole: build.query({
      query: (roleId) => ({ url: `/api/Users/ByRole/${roleId}` }),
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    // Notify the user(s) who can sign a slot: client note + logged request + email.
    requestSignature: build.mutation({
      query: (body) => ({
        url: `/api/PsychosocialAssessmentForm/RequestSignature`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SignatureHistory'],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    getClientUrinalysis: build.query({
      query: (clientId) => ({
        url: `/api/PsychosocialAssessmentForm/Urinalysis/${clientId}`,
      }),
      providesTags: ['Urinalysis'],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    // ── Client-level diagnoses (decoupled from assessment forms) ──────────────
    getClientDiagnoses: build.query({
      query: (clientId) => ({ url: `/api/ClientDiagnosis/Client/${clientId}` }),
      providesTags: (result, error, clientId) => [{ type: 'ClientDiagnosis', id: clientId }],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    getDiagnosisCodes: build.query({
      query: () => ({ url: `/api/ClientDiagnosis/Codes` }),
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    getClientDiagnosisHistory: build.query({
      query: (clientId) => ({ url: `/api/ClientDiagnosis/History/${clientId}` }),
      providesTags: (result, error, clientId) => [{ type: 'ClientDiagnosis', id: `history-${clientId}` }],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    upsertClientDiagnosis: build.mutation({
      query: (body) => ({ url: `/api/ClientDiagnosis`, method: 'POST', body }),
      invalidatesTags: (result, error, body) => [
        { type: 'ClientDiagnosis', id: body.clientId },
        { type: 'ClientDiagnosis', id: `history-${body.clientId}` },
      ],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    deleteClientDiagnosis: build.mutation({
      query: ({ id }) => ({ url: `/api/ClientDiagnosis/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { clientId }) => [
        { type: 'ClientDiagnosis', id: clientId },
        { type: 'ClientDiagnosis', id: `history-${clientId}` },
      ],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    // ── Client-level substance-use history (forms sync their substance lists into it) ──
    getClientSubstanceUse: build.query({
      query: (clientId) => ({ url: `/api/ClientSubstanceUse/Client/${clientId}` }),
      providesTags: (result, error, clientId) => [{ type: 'ClientSubstanceUse', id: clientId }],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    getClientSubstanceUseHistory: build.query({
      query: (clientId) => ({ url: `/api/ClientSubstanceUse/History/${clientId}` }),
      providesTags: (result, error, clientId) => [{ type: 'ClientSubstanceUse', id: `history-${clientId}` }],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    upsertClientSubstanceUse: build.mutation({
      query: (body) => ({ url: `/api/ClientSubstanceUse`, method: 'POST', body }),
      invalidatesTags: (result, error, body) => [
        { type: 'ClientSubstanceUse', id: body.clientId },
        { type: 'ClientSubstanceUse', id: `history-${body.clientId}` },
      ],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    deleteClientSubstanceUse: build.mutation({
      query: ({ id }) => ({ url: `/api/ClientSubstanceUse/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { clientId }) => [
        { type: 'ClientSubstanceUse', id: clientId },
        { type: 'ClientSubstanceUse', id: `history-${clientId}` },
      ],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.title }),
    }),
    // Both exports stream a file, so they go through fetch + saveAs rather than the base query.
    downloadUrinalysisReport: build.mutation({
      queryFn: async ({ clientId, format = "pdf", clientName }) => {
        try {
          const segment = format === "excel" ? "Excel" : "PDF";
          const response = await fetch(
            `${API_BASE_URL}/api/PsychosocialAssessmentForm/Urinalysis/${clientId}/${segment}`,
            {
              cache: "no-store",
              headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
            }
          );
          if (!response.ok) {
            throw new Error(`Failed to generate the urinalysis ${format === "excel" ? "spreadsheet" : "PDF"}`);
          }
          const blob = await response.blob();
          const safeName = String(clientName || `client_${clientId}`).replace(/[^\w-]+/g, "_");
          saveAs(blob, `Urinalysis_${safeName}_${moment().format("YYYYMMDD")}.${format === "excel" ? "xlsx" : "pdf"}`);
          return { data: { type: "urinalysis", format } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    // Medication list exports. Same fetch + saveAs shape as the urinalysis ones.
    downloadMedicationReport: build.mutation({
      queryFn: async ({ clientId, format = "pdf", clientName }) => {
        try {
          const segment = format === "excel" ? "Excel" : "PDF";
          const response = await fetch(
            `${API_BASE_URL}/api/ClientMedication/${clientId}/${segment}`,
            {
              cache: "no-store",
              headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
            }
          );
          if (!response.ok) {
            throw new Error(`Failed to generate the medication ${format === "excel" ? "spreadsheet" : "PDF"}`);
          }
          const blob = await response.blob();
          const safeName = String(clientName || `client_${clientId}`).replace(/[^\w-]+/g, "_");
          saveAs(blob, `Medications_${safeName}_${moment().format("YYYYMMDD")}.${format === "excel" ? "xlsx" : "pdf"}`);
          return { data: { type: "medications", format } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    // Batch assessment PDF — one combined document for many forms (used by the calendar list view).
    // items: [{ clientId, assessmentId, clientServiceId, formVersion }]
    downloadAssessmentBatchPdf: build.mutation({
      queryFn: async ({ items }) => {
        try {
          if (!items || items.length === 0) return { error: "No assessment forms to print." };
          const response = await fetch(
            `${API_BASE_URL}/api/PsychosocialAssessmentForm/PDF/Batch`,
            {
              method: "POST",
              cache: "no-store",
              headers: {
                "Content-Type": "application/json",
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
              body: JSON.stringify(items),
            }
          );
          if (!response.ok) {
            throw new Error("Failed to generate the batch assessment PDF.");
          }
          const blob = await response.blob();
          saveAs(blob, `AssessmentBatch_${moment().format("YYYYMMDD_HHmm")}.pdf`);
          return { data: { type: "assessmentBatch", count: items.length } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    getClientFormList: build.query({
      queryFn: async (clientId) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/PsychosocialAssessmentForm/ClientFormList/${clientId}`,
            {
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
            }
          );
          const data = await response.json();
          return { data: await data };
        } catch (e) {
          return { message: e.message };
        }
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getUsersRoles: build.query({
      query: () => ({
        url: `/api/Users/Roles`,
      }),
      // Refreshed by the role create / rename / delete / duplicate mutations below, so a new role
      // (e.g. Clinical Supervisor) shows up everywhere — including the form builder's signature
      // dropdown — without a full page reload.
      providesTags: ['Roles'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    // ── Role lifecycle (admin) ──
    createRole: build.mutation({
      invalidatesTags: ['Roles'],
      query: (name) => ({
        url: `/api/Users/Roles`,
        method: 'POST',
        body: { name },
      }),
      transformErrorResponse: (response) => {
        return { status: response?.status, message: response?.data?.message || response?.data?.Message || 'Failed to create role' };
      },
    }),
    renameRole: build.mutation({
      invalidatesTags: ['Roles'],
      query: ({ roleId, name }) => ({
        url: `/api/Users/Roles/${roleId}`,
        method: 'PUT',
        body: { name },
      }),
      transformErrorResponse: (response) => {
        return { status: response?.status, message: response?.data?.message || response?.data?.Message || 'Failed to rename role' };
      },
    }),
    deleteRole: build.mutation({
      invalidatesTags: ['Roles'],
      query: (roleId) => ({
        url: `/api/Users/Roles/${roleId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (response) => {
        return { status: response?.status, message: response?.data?.message || response?.data?.Message || 'Failed to delete role' };
      },
    }),
    cloneRole: build.mutation({
      invalidatesTags: ['Roles'],
      query: ({ roleId, name }) => ({
        url: `/api/Users/Roles/${roleId}/Clone`,
        method: 'POST',
        body: { name },
      }),
      transformErrorResponse: (response) => {
        return { status: response?.status, message: response?.data?.message || response?.data?.Message || 'Failed to duplicate role' };
      },
    }),
    // Get permissions assigned to a specific user (by UserId)
    getUserRolePermissions: build.query({
      query: (userId) => ({
        url: `/api/Users/RolePermissions/${userId}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title };
      },
    }),
    // Get permissions assigned to a specific role (by RoleId)
    getRolePermissions: build.query({
      // Accept either a plain roleId, or { roleId, userId } (userId is optional)
      query: (arg) => {
        const roleId = typeof arg === 'object' && arg !== null ? arg.roleId : arg;
        const userId = typeof arg === 'object' && arg !== null ? arg.userId : undefined;

        return {
          url: `/api/Users/RolePermissions/${roleId}`,
          ...(userId ? { params: { userId } } : {}),
        };
      },
      transformErrorResponse: (response) => {
        console.log("getRolePermissions error response:", response);
        return { message: response?.data?.Message || response?.data?.title };
      },
    }),
    // Update role permissions - PUT to RolePermissions/{roleId} with array payload
    updateRolePermissions: build.mutation({
      query: ({ roleId, permissions }) => ({
        url: `/api/Users/RolePermissions/${roleId}`,
        method: 'PUT',
        body: permissions,
      }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title };
      },
    }),
    getNotesTypes: build.query({
      query: () => ({
        url: `/api/QuickNote/Types`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getRequestSatatus: build.query({
      query: () => ({
        url: `/api/Utility/QuickNote/dropdown/RequestSatatus`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getNotes: build.mutation({
      query: (data) => {
        return {
          url: `/api/QuickNote/paginated`,
          method: "PUT",
          body: data,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    getNotesForPeriod: build.mutation({
      query: (data) => {
        return {
          url: `/api/QuickNote/OverTimePeriod`,
          method: "PUT",
          body: data,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    postNote: build.mutation({
      query: (data) => ({
        url: `/api/QuickNote`,
        method: "POST",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    editNote: build.mutation({
      query: ({ data, noteId }) => ({
        url: `/api/QuickNote/${noteId}`,
        method: "PATCH",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    deleteNote: build.mutation({
      query: ({ data, noteId }) => ({
        url: `/api/QuickNote/${noteId}`,
        method: "DELETE",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),

    checkEligibility: build.mutation({
      query: ({ clientId, data }) => ({
        url: `/api/Client/${clientId}/Eligibility`,
        method: "POST",
        body: data,
      }),
      // Keep Claim.MD's errors ([{ errorCode, errorMessage }]) — a 400 from this endpoint carries
      // them instead of a Message/title.
      transformErrorResponse: (response) => ({
        status: response.status,
        errors: response.data?.errors ?? response.data?.Errors ?? null,
        message: response.data?.Message || response.data?.message || response.data?.title || "Eligibility check failed",
      }),
    }),

    getClientAssignmentHistory: build.query({
      query: (data) => (data.clientId && {
        url: `/api/ClientAssignment/History/${data.clientId}?roleId=${data.roleId}`,
      }),
      providesTags: ['ClientAssignmentHistory'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    // Counselor change, step 1: the client's unfinished services the current counselor holds in the
    // change period (they move to the new / covering counselor). Nothing changes.
    // { clientId, newUserId, isTemporary, effectiveDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' | null }
    getCounselorChangePreview: build.query({
      query: ({ clientId, newUserId, isTemporary, effectiveDate, endDate }) => ({
        url: `/api/ClientAssignment/${clientId}/CounselorChange?newUserId=${encodeURIComponent(newUserId)}&effectiveDate=${encodeURIComponent(effectiveDate)}`
          + `&isTemporary=${isTemporary ? 'true' : 'false'}${isTemporary && endDate ? `&endDate=${encodeURIComponent(endDate)}` : ''}`,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: (response) => ({
        message: response.data?.message || response.data?.Message || 'Could not load the services to transfer.',
      }),
    }),
    // Temporary counselor assignments (covers) for the client's assignment grid.
    getClientTemporaryAssignments: build.query({
      query: (clientId) => ({ url: `/api/ClientAssignment/${clientId}/TemporaryAssignments` }),
      providesTags: ['ClientTemporaryAssignments'],
    }),
    // Counselor change, step 2: make the change and move the previewed services.
    // { clientId, newUserId, isTemporary, effectiveDate, endDate, expectedServiceCount }
    changeCounselor: build.mutation({
      query: ({ clientId, ...body }) => ({
        url: `/api/ClientAssignment/${clientId}/CounselorChange`,
        method: 'POST',
        body,
      }),
      // Not 'Client': refetching the client would reset the open client form (losing unsaved edits);
      // the assignments panel updates its row in place instead.
      invalidatesTags: ['ClientAssignmentHistory', 'ClientTemporaryAssignments', 'Service', 'PlanOfCare', 'PlanOfCarePreview', 'UserSchedule'],
      transformErrorResponse: (response) => ({
        message: response.data?.message || response.data?.Message || 'The counselor could not be changed.',
      }),
    }),
    getUsers: build.query({
      query: (roleId = '') => ({
        url: `/api/Users/All${roleId ? `?roleId=${roleId}` : ''}`,
      }),
      providesTags: ['Users'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getUserSchedule: build.query({
      query: () => ({
        url: `/api/Users/Schedule`,
      }),
      providesTags: ['UserSchedule'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    updateUserSchedule: build.mutation({
      query: (data) => {
        console.log("updateUserSchedule payload:", JSON.stringify(data, null, 2));
        return {
          url: `/api/Users/Schedule`,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: ['UserSchedule'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    deleteUserSchedule: build.mutation({
      query: (scheduleId) => ({
        url: `/api/Users/Schedule/${scheduleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserSchedule'],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    sendCoverageNotification: build.mutation({
      query: (data) => ({
        url: `/api/Users/CoverageNotification`,
        method: 'POST',
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    postAssessmentValues: build.mutation({
      query: (data) => ({
        url: `/api/PsychosocialAssessmentForm/SaveFieldValues/${data.clientId}?assessmentId=${data.formId}&clientServiceId=${data.clientServiceId}`,
        method: "POST",
        body: data.value,
      }),
      // A form's substance list syncs into the client's substance-use history.
      invalidatesTags: (result, error, data) => [
        { type: 'ClientSubstanceUse', id: data.clientId },
        { type: 'ClientSubstanceUse', id: `history-${data.clientId}` },
      ],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Saves one completed form's answers to every client in a session (group service).
    // targets: [{ clientId, clientServiceId }] — the session's attendees.
    postGroupAssessmentValues: build.mutation({
      // perClientValues: { [clientServiceId]: { [fieldId]: value } } — answers to "per client"
      // sections, written only to that client's own response.
      query: ({ assessmentId, values, targets, perClientValues }) => ({
        url: `/api/PsychosocialAssessmentForm/SaveGroupFieldValues?assessmentId=${assessmentId}`,
        method: "POST",
        body: { values, targets, perClientValues: perClientValues || null },
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    putUserValue: build.mutation({
      query: (data) => {
        // Log the body data before sending the request
        console.log('PUT request body:', data);

        return {
          url: `/api/Users/Me`,
          method: "PUT",
          body: data,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Manage Users "Active" checkbox. Inactive users drop out of the counselor/provider lists.
    // Manage Users only: every user, inactive included, so a deactivated user can be re-activated.
    // Every dropdown keeps using getUsers (active users only).
    getManagedUsers: build.query({
      query: () => ({ url: `/api/Users/All?includeInactive=true` }),
      providesTags: ['Users'],
      transformErrorResponse: (response) => ({ message: response.data?.Message || response.data?.message }),
    }),
    setUserActive: build.mutation({
      query: ({ id, active }) => ({
        url: `/api/Users/${id}/Active`,
        method: "PUT",
        body: active,
      }),
      transformErrorResponse: (response) => ({ message: response.data?.message || response.data?.Message || response.data?.title }),
    }),
    putUserRoleValue: build.mutation({
      query: (data) => {
        // Log the body data before sending the request
        console.log('PUT role request body:', data);

        return {
          url: `/api/Users/UpdateUserRole`,
          method: "PUT",
          body: data,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
      invalidatesTags: ['Users'],
    }),
    // Invite a new user (admin only): creates the app user keyed by email + role(s) and emails
    // a sign-in link. On first sign-in with their org Microsoft / @nlmbilling.com account the
    // token email matches this record and access is granted.
    inviteUser: build.mutation({
      query: (data) => ({
        url: `/api/Users`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Users'],
      transformErrorResponse: (response) => {
        return { message: response.data?.message || response.data?.Message || response.data?.title };
      },
    }),
    // Delete a user (admin only): removes the app DB record so the email can be re-invited.
    // The Entra identity is a separate directory object handled outside this call.
    deleteUser: build.mutation({
      query: (id) => ({
        url: `/api/Users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
      transformErrorResponse: (response) => {
        return { message: response.data?.message || response.data?.Message || response.data?.title };
      },
    }),
    // Records a sign-in for the current user. Fired once per browser session on login.
    recordLogin: build.mutation({
      query: () => ({
        url: `/api/Users/RecordLogin`,
        method: 'POST',
      }),
    }),
    getUserLoginHistory: build.query({
      query: (userId) => ({
        url: `/api/Users/${userId}/LoginHistory`,
      }),
    }),
    // Per-service non-billable flag (e.g. imported intakes): kept out of Billing and every claim.
    getServiceNonBillable: build.query({
      query: (clientServiceId) => `/api/ClientService/${clientServiceId}/NonBillable`,
      providesTags: (result, error, clientServiceId) => [{ type: "Service", id: `NonBillable-${clientServiceId}` }],
    }),
    putServiceNonBillable: build.mutation({
      query: ({ clientServiceId, nonBillable }) => ({
        url: `/api/ClientService/${clientServiceId}/NonBillable`,
        method: "PUT",
        body: { nonBillable },
      }),
      invalidatesTags: (result, error, { clientServiceId }) => [{ type: "Service", id: `NonBillable-${clientServiceId}` }],
    }),
    putBillingStatus: build.mutation({
      query: (data) => {
        console.log("status data", data.newRecord.serviceId);
        return {
          url: `/api/ClientService/Update/BillingStatus`,
          method: "POST",
          body: {
            id: 0, // Increment ID (use a better ID mechanism if available)
            serviceId: data.newRecord.serviceId,
            statusId: data.newRecord.statusId,
            note: data.newRecord.note ?? "",
          },
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Balance invoice PDF (QuestPDF). Pass the ClientService ids to include:
    // one id → per-service invoice; a client's selected ids → per-client aggregate.
    downloadInvoicePDF: build.mutation({
      queryFn: async ({ clientServiceIds, fileName }) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/ClientService/GetInvoicePDF`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
                "Content-Type": "application/json",
              },
              method: "POST",
              body: JSON.stringify({ clientServiceIds }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to generate invoice");
          }

          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/pdf")) {
            const blob = await response.blob();
            // Open in a new tab for print preview rather than force-downloading.
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener");
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            return { data: { type: "invoice" } };
          }
          const json = await response.json();
          return { data: { type: "no_records", message: json.message || "No invoiceable services found." } };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    // Persist a manually-applied payment (amount, date, check#, deductible,
    // patient responsibility, method) and roll the service's billing status forward.
    applyPayment: build.mutation({
      query: (payment) => ({
        url: `/api/ClientService/ApplyPayment`,
        method: "POST",
        body: {
          clientServiceId: payment.clientServiceId,
          paidAmount: payment.paidAmount ?? 0,
          paidDate: payment.paidDate || null,
          checkNumber: payment.checkNumber || null,
          transactionNumber: payment.transactionNumber || null,
          paymentMethod: payment.paymentMethod ?? null,
          deductibleAmount: payment.deductibleAmount ?? 0,
          patientResponsibilityAmount: payment.patientResponsibilityAmount ?? 0,
        },
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    getClientService: build.query({
      query: (clientId) => ({
        url: `/api/ClientService/${clientId}`,
      }),
      providesTags: (result) => ["Service"],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    getClientServiceByServiceId: build.query({
      query: (serviceId) => ({
        url: `/api/ClientService/GetClientService/${serviceId}`,
      }),
      // Tag this service's detail so edits/deletes refetch it (otherwise the reopened
      // edit drawer shows stale room/topic/track from cache after a save).
      providesTags: (result, error, serviceId) => [{ type: 'Service', id: serviceId }],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    getClientServiceStatistic: build.query({
      query: (clientId) => ({
        url: `/api/ClientService/GetClientServiceStatistic/${clientId}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAssignmentClients: build.mutation({
      query: (data) => {
        console.log('Request body sent:', data);
        return {
          url: `/api/ClientAssignment/GetAssignmentClients`,
          method: "POST",
          body: data,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getServices: build.query({
      query: () => ({
        url: `/api/Utility/ClientService/dropdown/Service`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getEvents: build.mutation({
      query: (data) => ({
        url: `/api/EventCalendar/GetEvents`,
        method: "POST",
        body: data
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientMedicationMethod: build.query({
      query: () => ({
        url: `/api/ClientMedication/dropdown/Method`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientMedicationFrequency: build.query({
      query: () => ({
        url: `/api/ClientMedication/dropdown/Frequency`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientMedicationDoseType: build.query({
      query: () => ({
        url: `/api/ClientMedication/dropdown/DoseType`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientMedicationPrescriber: build.query({
      query: () => ({
        url: `/api/ClientMedication/dropdown/Prescriber`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    createClientService: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/SaveClientService`,
        method: "POST",
        body: data.data,
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {

        console.log("Request Data:", data); // Log the data being sent to the mutation
        try {
          const { data: id } = await queryFulfilled

          const newData = data.data
          // newData.id = id
          dispatch(
            clientAPI.util.updateQueryData("getClientService", data.clientId, (draft) => {
              newData.startDate = new Date(newData.startDate).toISOString().slice(0, -1)
              newData.endDate = new Date(newData.endDate).toISOString().slice(0, -1)
              draft.push(newData)
            })
          );
        } catch (e) {
          console.error(e);
        }
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Preview the authorizations needed to cover services not yet linked to one.
    getNeededAuthorizations: build.query({
      query: () => ({
        url: `/api/ClientService/NeededAuthorizations`,
      }),
      providesTags: ['NeededAuthorizations'],
      transformErrorResponse: (response) => ({
        message: response?.data?.Message || response?.data?.title,
      }),
    }),
    // Create the needed authorizations and link the unauthorized services to them.
    generateNeededAuthorizations: build.mutation({
      query: () => ({
        url: `/api/ClientService/NeededAuthorizations/Generate`,
        method: 'POST',
      }),
      invalidatesTags: ['NeededAuthorizations'],
      transformErrorResponse: (response) => ({
        message: response?.data?.Message || response?.data?.title,
      }),
    }),
    // Set/clear a service's attendance outcome (dbo.ClientService.AttendanceStatusId).
    updateClientServiceAttendance: build.mutation({
      query: ({ clientServiceId, attendanceStatusId }) => ({
        url: `/api/ClientService/${clientServiceId}/Attendance`,
        method: "PUT",
        body: { attendanceStatusId: attendanceStatusId ?? null },
      }),
      transformErrorResponse: (response) => ({
        message: response?.data?.Message || response?.data?.title,
      }),
    }),
    upsertClientService: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/UpsertClientService`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'Service', id: data.id },
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    updateClientService: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/SaveClientService`,
        method: "POST",
        body: data,
      }),
      // Refetch this service's detail so a reopened edit drawer shows the saved
      // room/topic/track (not stale cache).
      invalidatesTags: (result, error, data) => [{ type: 'Service', id: data?.id }],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          dispatch(
            clientAPI.util.updateQueryData("getClientService", data.clientId, (draft) => {
              const service = draft.find((item) => item.id === data.id)
              service.description = data.description
              service.serviceId = data.serviceId
              service.name = data.name
              service.startDate = new Date(data.startDate).toISOString().slice(0, -1)
              service.endDate = new Date(data.endDate).toISOString().slice(0, -1)
              service.materials = data.materials
            })
          );
        } catch (e) {
          console.error(e);
        }
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    // Apply shared-detail changes to every occurrence in a linked group in one request
    // (a single set-based UPDATE server-side), instead of looping SaveClientService per row.
    updateClientServiceGroup: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/UpdateClientServiceGroup`,
        method: "POST",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    // Bulk-delete every occurrence of a service in a linked group (blocks if any is billed/paid).
    deleteClientServiceGroup: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/DeleteClientServiceGroup`,
        method: "POST",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    // Regenerate a group's occurrences from a new schedule/recurrence (blocks if any is billed/paid).
    rescheduleClientServiceGroup: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/RescheduleClientServiceGroup`,
        method: "POST",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    // Move every occurrence of a service in a group by a whole number of days (drag whole series).
    shiftClientServiceGroup: build.mutation({
      query: (data) => ({
        url: `/api/ClientService/ShiftClientServiceGroup`,
        method: "POST",
        body: data,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    // Set a service's status (e.g. mark Performed) from the calendar.
    updateClientServiceStatus: build.mutation({
      query: ({ clientServiceId, statusId }) => ({
        url: `/api/ClientService/${clientServiceId}/Status`,
        method: "PUT",
        body: { statusId },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Service', id: arg?.clientServiceId }],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    deleteClientService: build.mutation({
      query: (clientServiceId) => ({
        url: `/api/ClientService/DeleteClientService/${clientServiceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, clientServiceId) => [{ type: 'Service', id: clientServiceId }],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    getGroupName: build.query({
      query: () => ({
        url: `/api/clientService/dropdown/groupName`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getCanceledBillingStatus: build.query({
      query: () => ({
        // url: `/api/ClientService/dropdown/CanceledBillingStatus`,
        url: `/api/ClientService/dropdown/BillingStatus`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getGroupName: build.query({
      query: () => ({
        url: `/api/clientService/dropdown/groupName`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getTopic: build.query({
      query: () => ({
        url: `/api/clientService/dropdown/topic`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    // NOTE: getTracks lives in UtilityService (/api/utility/GetTracks). A duplicate here that
    // pointed at the non-existent /api/clientService/dropdown/track won the RTK endpoint-name
    // collision and made every Track dropdown return nothing — removed so the Utility one wins.
    // Linked service pairs (e.g. Per Diem ↔ Room & Board). Returns [{ serviceId, linkedServiceId }].
    getServiceLinks: build.query({
      query: () => ({
        url: `/api/clientService/dropdown/ServiceLinks`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),
    getUrineScreenTestResult: build.query({
      query: () => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/UrineScreenTestResult`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getUrineScreenSubstance: build.query({
      query: () => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/UrineScreenSubstance`,
      }),
      // Tag-based invalidation replaces the old trick of passing the last mutation's result as a
      // query arg purely to bust the cache key.
      providesTags: ['UrineScreenSubstance'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    editUrineScreenSubstance: build.mutation({
      query: (data) => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/EditUrineScreenSubstance`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['UrineScreenSubstance'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    removeUrineScreenSubstance: build.mutation({
      query: (substanceId) => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/RemoveUrineScreenSubstance/${substanceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ['UrineScreenSubstance'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    deleteUrineScreenFile: build.mutation({
      query: (fileId) => ({
        url: `/api/PsychosocialAssessmentForm/FormFile/${fileId}`,
        method: "DELETE",
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    getRoom: build.query({
      query: () => ({
        url: `/api/clientService/dropdown/room`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getDrugOfChoice: build.query({
      query: () => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/DrugOfChoice`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAbusedSubstances: build.query({
      query: () => ({
        url: `/api/Utility/PsychosocialAssessmentForm/dropdown/AbusedSubstances`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getListOfAllergies: build.query({
      query: () => ({
        url: `/api/Utility/Allergy/dropdown/ListOfAllergies`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAllergySeverity: build.query({
      query: () => ({
        url: `/api/Utility/Allergy/dropdown/AllergySeverity`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAllergyCodeSystems: build.query({
      query: () => ({
        url: `/api/Utility/Allergy/dropdown/AllergyCodeSystems`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getLevelOfCare: build.query({
      query: () => ({
        url: `/api/Utility/ClientBasicInformation/dropdown/LevelOfCare`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAuthorizationList: build.query({
      query: () => ({
        url: `/api/ClientService/dropdown/Authorization`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getBillingStatus: build.query({
      query: () => ({
        url: `/api/ClientService/dropdown/BillingStatus`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getServiceStatus: build.query({
      query: () => ({
        url: `/api/ClientService/dropdown/Status`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getMunicipality: build.query({
      query: () => ({
        url: `/api/Utility/Client/dropdown/Municipality`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAppReminderPref: build.query({
      query: () => ({
        url: `/api/Utility/Client/dropdown/AppReminderPref`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientServiceHistory: build.query({
      query: (clientServiceId) => ({
        url: `/api/ClientService/GetClientServiceHistory/${clientServiceId}`,
      }),
      // Tag by service so a status change (upsert/update/delete invalidate this) refetches the
      // history — otherwise the status-history dialog shows a stale cached list.
      providesTags: (result, error, clientServiceId) => [{ type: 'Service', id: clientServiceId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message,
      }),
    }),
    getClientServiceBillingHistory: build.query({
      query: (data) => ({
        url: `/api/ClientService/GetClientServiceBillingHistory/${data.serviceId}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getERA: build.query({
      query: (processedStatusId) => ({
        url: `/api/ClientService/GetERAs/${processedStatusId}`,
      }),
      transformErrorResponse: (response) => {
        // Log the full response for debugging
        console.log('Error response from getERA:', response);
        return { message: response.data?.Message || 'An error occurred' };
      },
    }),
    // Add this to your ClientService or relevant service file
    downloadFileERAOld: build.mutation({
      query: (ERAId) => ({
        url: `/api/ClientService/GetERAPDF/${ERAId}`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (response) => response, // Pass the response as is for blob data
      transformErrorResponse: (response) => {
        console.log('Error response from DownloadFileERA:', response);
        return { message: response.data?.Message || 'An error occurred' };
      },
    }),
    downloadFileERA: build.mutation({
      queryFn: async (body) => {
        const { ERAId } = body;
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/ClientService/GetERAPDF/${ERAId}`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
              method: "GET",
            }
          );

          console.log('ERAid:', ERAId);

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          // Retrieve the PDF blob data
          const blob = await response.blob();

          // Use `saveAs` from file-saver to initiate the download with a specific filename
          saveAs(blob, `ERA_${ERAId}_${moment().format('YYYY-MM-DD')}.pdf`);

          return { data: 'File downloaded successfully' };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
    saveClientAuthorization: build.mutation({
      query: (clientAuthorization) => {
        console.log("clientAuthorization object before API call:", clientAuthorization); // Log the object here
        return {
          url: `/api/ClientAuthorization/SaveClientAuthorization`,
          method: "POST",
          body: clientAuthorization,
        };
      },
      transformErrorResponse: (response) => {
        return {
          message: response.data?.Message || response.data?.title || 'An error occurred'
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientAuthorization', id: arg?.Id || arg?.id },
      ],
    }),
    getClientAuthorization: build.query({
      query: (authorizationId) => ({
        url: `/api/ClientAuthorization/${authorizationId}`,
      }),
      providesTags: (result, error, authorizationId) => [
        { type: 'ClientAuthorization', id: authorizationId },
      ],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    // Oldest service that still needs work on the Billing / Authorization pages (null when none),
    // plus the clinic's today. Drives those pages' default Start/Through dates.
    getDefaultDateRanges: build.query({
      query: () => ({ url: `/api/ClientService/DefaultDateRanges` }),
      keepUnusedDataFor: 0,
      transformErrorResponse: (response) => ({ message: response.data?.Message }),
    }),
    // A client's primary insurance ({ clientInsuranceId, insuranceCompanyId, insuranceName }), or
    // null when none is on file. Prefills a new authorization.
    getPrimaryInsurance: build.query({
      query: (clientId) => ({
        url: `/api/ClientAuthorization/PrimaryInsurance/${clientId}`,
        responseHandler: async (response) => (response.status === 204 ? null : response.json()),
      }),
      transformErrorResponse: (response) => ({ message: response.data?.Message }),
    }),
    getAuthorizationLinkedServices: build.query({
      query: (authorizationId) => ({
        url: `/api/ClientAuthorization/${authorizationId}/LinkedServices`,
      }),
      providesTags: (result, error, authorizationId) => [
        { type: 'ClientAuthorization', id: authorizationId },
      ],
      transformErrorResponse: (response) => {
        return { message: response.data?.Message };
      },
    }),
    getAuthorizationStatuses: build.query({
      query: () => ({
        url: `/api/ClientAuthorization/dropdown/AuthorizationStatuses`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAuthorizationDenialReasons: build.query({
      query: () => ({
        url: `/api/ClientAuthorization/dropdown/AuthorizationDenials`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getAuthorizationReport: build.mutation({
      query: (body) => {
        try {
          // Log to show billingStatus filter being passed
          console.log("AuthorizationStatus filter:", body);

          return {
            url: `/api/ClientAuthorization/GetAuthorizationReport`,
            method: "POST",
            body: {
              recordNumber: body.recordNumber,
              offset: body.offset,
              clientIdList: body.clientIdList || null,
              orderBy: body.orderBy,
              orderByDirection: body.order,
              requestDate: body.requestDate || null,
              requestEndDate: body.requestEndDate || null,
              levelOfCareIdList: body.levelOfCare || null,
              authorizationStatusIdList: body.authorizationStatus || null,
              authorizationList: body.authorizationList || null,
            },
          };
        } catch (error) {
          console.log("Error building query:", error);
          throw new Error("Failed to build the request. Please check the input data.");
        }
      },
      transformErrorResponse: (response) => {
        // Log detailed error response
        console.error("Authorization Report response error:", response);

        // Handle specific error cases
        if (response.status === 400) {
          return { message: "Bad Request: Please verify the input data." };
        }
        if (response.status === 404) {
          return { message: "The requested resource was not found." };
        }
        if (response.status === 500) {
          return { message: "Server Error: Please try again later." };
        }

        // Default error message
        console.log("message", response);
        return { message: response.data?.Message || "An unknown error occurred." };
      },
    }),
    downloadFile278: build.mutation({
      queryFn: async (body) => {
        try {
          // Log the filters
          console.log('Download 278 File Filters:', {
            recordNumber: body.recordNumber,
            offset: body.offset,
            clientIdList: body.clientIdList || null,
            orderBy: body.orderBy,
            orderByDirection: body.order,
            serviceDate: body.serviceDate || null,
            endDate: body.endDate || null,
            serviceIdList: body.serviceIdList || null,
            levelOfCareIdList: body.levelOfCare || null,
            billingStatusIdList: body.billingStatus || null,
          });

          const response = await fetch(
            `${API_BASE_URL}/api/ClientAuthorization/Download/278File`,
            {
              cache: "no-store",
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
                "Content-Type": "application/json",
              },
              method: "POST",
              body: JSON.stringify({
                recordNumber: body.recordNumber,
                offset: body.offset,
                clientIdList: body.clientIdList || null,
                orderBy: body.orderBy,
                orderByDirection: body.order,
                serviceDate: body.serviceDate || null,
                endDate: body.endDate || null,
                serviceIdList: body.serviceIdList || null,
                levelOfCareIdList: body.levelOfCare || null,
                billingStatusIdList: body.billingStatus || null,
              }),
            }
          );

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          const blob = await response.blob();
          saveAs(blob, `278(${moment(new Date()).utc().format('MM/DD/YYYY')}).csv`);

          return { data: 'File downloaded successfully' };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),

    // ── Plan of Care ──────────────────────────────────────────────────────────
    getClientPlanOfCare: build.query({
      query: (clientId) => ({
        url: `/api/clients/${clientId}/planofcare`,
      }),
      providesTags: (result, error, clientId) => [{ type: 'PlanOfCare', id: clientId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    savePlanOfCare: build.mutation({
      query: (data) => ({
        url: `/api/planofcare`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PlanOfCare', id: arg?.clientID }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    getPlanOfCare: build.query({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}`,
      }),
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    generatePlanOfCarePreview: build.mutation({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}/generate-preview`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, planOfCareId) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    getPlanOfCarePreview: build.query({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}/preview`,
      }),
      providesTags: (result, error, planOfCareId) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    confirmPlanOfCarePreview: build.mutation({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}/confirm-preview`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, planOfCareId) => [
        { type: 'PlanOfCarePreview', id: planOfCareId },
        'Service',
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    cancelPlanOfCarePreview: build.mutation({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}/cancel-preview`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, planOfCareId) => [
        { type: 'PlanOfCarePreview', id: planOfCareId },
        { type: 'PlanOfCare', id: planOfCareId },
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    // What "Delete all generated scheduled services and regenerate" deletes and keeps:
    // { deleteCount, firstDate, lastDate, deleted[], addedCount, added[], keptCount, kept[] }.
    getPlanOfCareRegenerateImpact: build.query({
      query: ({ planOfCareId, includeAdded }) => ({
        url: `/api/planofcare/${planOfCareId}/regenerate?includeAdded=${includeAdded ? 'true' : 'false'}`,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    // Deletes the plan's unfinished generated services and generates a new preview from the
    // plan's current dates (the user then reviews and confirms it).
    regeneratePlanOfCarePreview: build.mutation({
      query: ({ planOfCareId, includeAdded }) => ({
        url: `/api/planofcare/${planOfCareId}/regenerate?includeAdded=${includeAdded ? 'true' : 'false'}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { planOfCareId }) => [
        { type: 'PlanOfCarePreview', id: planOfCareId },
        { type: 'PlanOfCare', id: planOfCareId },
        'Service',
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    // CLT-DCA-007: what discharging with this last date of service still needs (completed
    // discharge paperwork on that date) and how many scheduled services after it get canceled.
    getDischargeCheck: build.query({
      query: ({ clientId, lastDateOfService }) => ({
        url: `/api/Client/${clientId}/DischargeCheck?lastDateOfService=${lastDateOfService}`,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    // Discharge: account → Discharged with the last date of service, schedule after it canceled,
    // open plan of care closed. Refused (400) until the discharge paperwork is completed.
    dischargeClient: build.mutation({
      query: ({ clientId, data }) => ({
        url: `/api/Client/${clientId}/Discharge`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        'Client',
        'ClientHistory',
        { type: 'PlanOfCare', id: arg?.clientId },
        'Service',
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    savePlanOfCareEndDateDecision: build.mutation({
      query: ({ planOfCareId, data }) => ({
        url: `/api/planofcare/${planOfCareId}/end-date-decision`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'PlanOfCare', id: arg?.clientId },
        'Service',
      ],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    getPlanOfCareEventLog: build.query({
      query: (planOfCareId) => ({
        url: `/api/planofcare/${planOfCareId}/event-log`,
      }),
      providesTags: (result, error, planOfCareId) => [{ type: 'PlanOfCareEventLog', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    addAdditionalServicePreview: build.mutation({
      query: ({ planOfCareId, data }) => ({
        url: `/api/planofcare/${planOfCareId}/additional-service-preview`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { planOfCareId }) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    deleteAdditionalServiceGroup: build.mutation({
      query: ({ planOfCareId, clientServiceGroupId }) => ({
        url: `/api/planofcare/${planOfCareId}/additional-service-group/${clientServiceGroupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { planOfCareId }) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    deleteAdditionalServiceRow: build.mutation({
      // Deletes a SINGLE preview occurrence by its own row id — distinct from
      // deleteAdditionalServiceGroup, which removes every occurrence in a group.
      query: ({ planOfCareId, planOfCareSchedulePreviewId }) => ({
        url: `/api/planofcare/${planOfCareId}/additional-service-row/${planOfCareSchedulePreviewId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { planOfCareId }) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    rescheduleAdditionalServiceGroup: build.mutation({
      // Regenerates all occurrences in a preview group from a new schedule/recurrence,
      // keeping the same clientServiceGroupId. Server expands the rule atomically.
      query: ({ planOfCareId, clientServiceGroupId, data }) => ({
        url: `/api/planofcare/${planOfCareId}/reschedule-service-group/${clientServiceGroupId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { planOfCareId }) => [{ type: 'PlanOfCarePreview', id: planOfCareId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    downloadPlanOfCareExcel: build.mutation({
      queryFn: async (planOfCareId) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/planofcare/${planOfCareId}/report/excel`,
            {
              method: 'GET',
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error('Failed to generate Excel report');
          }
          const blob = await response.blob();
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename = match
            ? match[1].replace(/['"]/g, '')
            : `PlanOfCare_${planOfCareId}_${moment().format('YYYYMMDD')}.xlsx`;
          saveAs(blob, filename);
          return { data: { success: true } };
        } catch (e) {
          return { error: { message: e.message } };
        }
      },
    }),
    downloadPlanOfCareReport: build.mutation({
      queryFn: async (planOfCareId) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/planofcare/${planOfCareId}/report`,
            {
              method: 'GET',
              headers: {
                authorization: `Bearer ${getTokenFromLocalStorage()}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error('Failed to generate report');
          }
          const blob = await response.blob();
          const disposition = response.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename = match
            ? match[1].replace(/['"]/g, '')
            : `PlanOfCare_${planOfCareId}_${moment().format('YYYYMMDD')}.pdf`;
          saveAs(blob, filename);
          return { data: { success: true } };
        } catch (e) {
          return { error: { message: e.message } };
        }
      },
    }),
    // ── Client Documents ──────────────────────────────────────────────────
    getClientDocuments: build.query({
      query: (clientId) => ({
        url: `/api/Client/${clientId}/Documents`,
      }),
      providesTags: (result, error, clientId) => [{ type: 'ClientDocuments', id: clientId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    uploadClientDocument: build.mutation({
      query: ({ clientId, formData }) => ({
        url: `/api/Client/${clientId}/Documents`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'ClientDocuments', id: clientId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    deleteClientDocument: build.mutation({
      query: ({ clientFileAttachmentId, clientId }) => ({
        url: `/api/Client/Documents/${clientFileAttachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { clientId }) => [{ type: 'ClientDocuments', id: clientId }],
      transformErrorResponse: (response) => ({
        message: response.data?.Message || response.data?.title,
      }),
    }),
    downloadClientDocument: build.mutation({
      queryFn: async (clientFileAttachmentId) => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/Client/Documents/${clientFileAttachmentId}/Download`,
            {
              headers: { authorization: `Bearer ${getTokenFromLocalStorage()}` },
            }
          );
          if (!response.ok) throw new Error('Download failed');
          const blob = await response.blob();
          return { data: blob };
        } catch (e) {
          return { error: e.message };
        }
      },
    }),
  }),
});
export const {
  useFetchClientQuery,
  useGetClientHistoryQuery,
  usePostClientMutation,
  useImportIntakeMutation,
  useGetClientsMutation,
  useGetProfilePictureQuery,
  useGetAssessmentFormQuery,
  useGetAssessmentFormsQuery,
  useGetAssessmentFormTypesQuery,
  useGetAssessmentFormHistoryQuery,
  useGetFormVersionHistoryQuery,
  useUpdateAssessmentFormMutation,
  useRevertAssessmentFormVersionMutation,
  useGetSearchPossibleValuesQuery,
  useGetAssessmentValuesQuery,
  useLazyGetAssessmentFormQuery,
  useLazyGetAssessmentValuesQuery,
  useGetPossibleValueByIdQuery,
  usePostAssessmentValuesMutation,
  useGetClientFormListQuery,
  useDownloadAssessmentPDFMutation,
  useGetClientUrinalysisQuery,
  useGetClientDiagnosesQuery,
  useGetDiagnosisCodesQuery,
  useGetClientDiagnosisHistoryQuery,
  useUpsertClientDiagnosisMutation,
  useDeleteClientDiagnosisMutation,
  useGetClientSubstanceUseQuery,
  useGetClientSubstanceUseHistoryQuery,
  useUpsertClientSubstanceUseMutation,
  useDeleteClientSubstanceUseMutation,
  useDownloadUrinalysisReportMutation,
  useGetSignatureHistoryQuery,
  useGetUsersByRoleQuery,
  useRequestSignatureMutation,
  useDownloadMedicationReportMutation,
  useDownloadAssessmentBatchPdfMutation,
  useGetUsersRolesQuery,
  useCreateRoleMutation,
  useRenameRoleMutation,
  useDeleteRoleMutation,
  useCloneRoleMutation,
  useGetUserRolePermissionsQuery,
  useGetRolePermissionsQuery,
  useUpdateRolePermissionsMutation,
  useGetUsersQuery,
  useGetManagedUsersQuery,
  useGetUserScheduleQuery,
  useUpdateUserScheduleMutation,
  useDeleteUserScheduleMutation,
  useSendCoverageNotificationMutation,
  useGetAssessmentFormSignaturesQuery,
  usePutUserValueMutation,
  usePutUserRoleValueMutation,
  useSetUserActiveMutation,
  useInviteUserMutation,
  useDeleteUserMutation,
  useRecordLoginMutation,
  useGetUserLoginHistoryQuery,
  useGetClientServiceQuery,
  useGetClientServiceByServiceIdQuery,
  useCreateClientServiceMutation,
  useUpdateClientServiceMutation,
  useUpdateClientServiceGroupMutation,
  useDeleteClientServiceGroupMutation,
  useRescheduleClientServiceGroupMutation,
  useShiftClientServiceGroupMutation,
  useUpdateClientServiceStatusMutation,
  useUpdateClientServiceAttendanceMutation,
  useGetNeededAuthorizationsQuery,
  useLazyGetNeededAuthorizationsQuery,
  useGenerateNeededAuthorizationsMutation,
  useDeleteClientServiceMutation,
  useUpsertClientServiceMutation,
  useGetClientServiceStatisticQuery,
  useSignFormMutation,
  useGetUserInfoQuery,
  useGetMyDailyAlertsQuery,
  useUnsignFormMutation,
  usePostGroupAssessmentValuesMutation,
  useSignGroupFormMutation,
  useRemoveGroupFormSignatureMutation,
  useGetClientAssignmentHistoryQuery,
  useGetNotesTypesQuery,
  usePostNoteMutation,
  useGetNotesMutation,
  useGetNotesForPeriodMutation,
  useEditNoteMutation,
  useDeleteNoteMutation,
  useCheckEligibilityMutation,
  useGetEventsMutation,
  useGetAssignmentClientsMutation,
  useGetGroupNameQuery,
  useGetRoomQuery,
  useGetTopicQuery,
  useGetServiceLinksQuery,
  useGetRequestSatatusQuery,
  useGetUrineScreenTestResultQuery,
  useGetUrineScreenSubstanceQuery,
  useEditUrineScreenSubstanceMutation,
  useRemoveUrineScreenSubstanceMutation,
  useDeleteUrineScreenFileMutation,
  useGetClientDocumentsQuery,
  useUploadClientDocumentMutation,
  useDeleteClientDocumentMutation,
  useDownloadClientDocumentMutation,
  useGetClientMedicationDoseTypeQuery,
  useGetClientMedicationFrequencyQuery,
  useGetClientMedicationMethodQuery,
  useGetClientMedicationPrescriberQuery,
  useGetDrugOfChoiceQuery,
  useGetAbusedSubstancesQuery,
  useGetAllergyCodeSystemsQuery,
  useGetAllergySeverityQuery,
  useGetListOfAllergiesQuery,
  useGetBillingReportMutation,
  useGetScheduleMutation,
  useGetFormApplyToNotesPreviewQuery,
  useApplyFormToNotesMutation,
  useGetCounselorChangePreviewQuery,
  useChangeCounselorMutation,
  useGetClientTemporaryAssignmentsQuery,
  useGetServiceNonBillableQuery,
  usePutServiceNonBillableMutation,
  useGetClientServiceHistoryQuery,
  useGetClientServiceBillingHistoryQuery,
  useGetLevelOfCareQuery,
  useGetAuthorizationListQuery,
  useGetBillingStatusQuery,
  useGetServicesQuery,
  useGetServiceStatusQuery,
  useDownloadFile837Mutation,
  useConfirmDownloadFile837Mutation,
  useSendFile837Mutation,
  useConfirmSendFile837Mutation,
  useCheckClaimStatusMutation,
  useGetClaimDataPreviewMutation,
  useDownloadClaimPdfMutation,
  useDownloadClaimsExcelMutation,
  useGetERAFilesMutation,
  useGetMunicipalityQuery,
  useGetAppReminderPrefQuery,
  usePostUpload835FileMutation,
  useGetCanceledBillingStatusQuery,
  usePutBillingStatusMutation,
  useDownloadInvoicePDFMutation,
  useApplyPaymentMutation,
  useGetERAQuery,
  useDownloadFileERAMutation,
  useSaveClientAuthorizationMutation,
  useGetClientAuthorizationQuery,
  useGetAuthorizationLinkedServicesQuery,
  useGetDefaultDateRangesQuery,
  useLazyGetPrimaryInsuranceQuery,
  useGetAuthorizationStatusesQuery,
  useGetAuthorizationReportMutation,
  useGetAuthorizationDenialReasonsQuery,
  useDownloadFile278Mutation,
  useGetClientPlanOfCareQuery,
  useSavePlanOfCareMutation,
  useGetPlanOfCareQuery,
  useGeneratePlanOfCarePreviewMutation,
  useGetPlanOfCarePreviewQuery,
  useConfirmPlanOfCarePreviewMutation,
  useCancelPlanOfCarePreviewMutation,
  useGetPlanOfCareRegenerateImpactQuery,
  useRegeneratePlanOfCarePreviewMutation,
  useSavePlanOfCareEndDateDecisionMutation,
  useGetDischargeCheckQuery,
  useDischargeClientMutation,
  useGetPlanOfCareEventLogQuery,
  useAddAdditionalServicePreviewMutation,
  useRescheduleAdditionalServiceGroupMutation,
  useDeleteAdditionalServiceGroupMutation,
  useDeleteAdditionalServiceRowMutation,
  useDownloadPlanOfCareReportMutation,
  useDownloadPlanOfCareExcelMutation,
} = clientAPI;
